import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import EnvironmentIndicator from "@/components/EnvironmentIndicator";
import { apiUrl } from "@/lib/apiUrl";
import { detectDeploymentStage } from "@/lib/environment";
import { usePageTitle } from "@/hooks/usePageTitle";
import { trpc } from "@/lib/trpc";
import { resolvePortalEntryRoute } from "@/lib/portalShell";
import TrainingVideoPlayer from "@/components/TrainingVideoPlayer";
import { ArrowRight, Layers3 } from "lucide-react";
import { toast } from "sonner";

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? "1.0.0";
const STAGE_LABEL_BY_CODE = {
  DEV: "desenvolvimento",
  TST: "teste",
  HML: "homologação",
  PRD: "produção",
} as const;

export default function Login() {
  usePageTitle("Login do Portal da Jornada do Cliente");
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const localLoginMutation = trpc.auth.localLogin.useMutation();
  const loginVideosQuery = trpc.trainingVideos.getLoginVideos.useQuery();
  const welcomeVideo = loginVideosQuery.data?.videos?.[0] ?? null;
  const hostname = typeof window !== "undefined" ? window.location.hostname : "localhost";
  const environmentStage = detectDeploymentStage(hostname);
  const environmentLabel = STAGE_LABEL_BY_CODE[environmentStage];

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(apiUrl("/api/trpc/auth.me"), { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          const user = data?.result?.data?.json;
          if (user) {
            setLocation(resolvePortalEntryRoute());
            return;
          }
        }
      } catch (_error) {
        // Sem sessão ativa: permanece na tela de login.
      }
      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [setLocation]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Preencha email e senha");
      return;
    }

    setIsLoggingIn(true);
    try {
      await localLoginMutation.mutateAsync({ email, password });
      toast.success("Login realizado com sucesso!");
      window.location.href = resolvePortalEntryRoute();
    } catch (_error: any) {
      toast.error("Não foi possível entrar. Verifique suas credenciais.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_#13325d_0%,_#08213f_45%,_#031426_100%)] p-4">
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#C0FF00] text-[#08213f] shadow-lg">
            <Layers3 className="h-7 w-7" />
          </div>
          <p className="text-sm text-white/70">Carregando o Portal da Jornada do Cliente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,_#13325d_0%,_#08213f_45%,_#031426_100%)] p-4">
      <div className="absolute right-[-120px] top-[-120px] h-80 w-80 rounded-full bg-[#C0FF00]/10 blur-3xl" />
      <div className="absolute bottom-[-140px] left-[-100px] h-96 w-96 rounded-full bg-sky-400/10 blur-3xl" />

      <div className="relative grid w-full max-w-5xl overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl lg:grid-cols-[1.1fr_0.9fr]">
        <section className="hidden bg-[#08213f] px-10 py-12 text-white lg:flex lg:flex-col lg:justify-between">
          <div className="space-y-6">
            <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/80">
              <Layers3 className="h-3.5 w-3.5" />
              Portal da Jornada do Cliente
            </div>
            <div className="space-y-4">
              <h1 className="max-w-md text-4xl font-semibold leading-tight">
                Entre no portal e siga para o que precisa fazer hoje.
              </h1>
              <p className="max-w-xl text-sm leading-7 text-white/75">
                Aqui você acessa o Portal da Jornada do Cliente e continua sua rotina com mais clareza, seja na gestão da conta
                enquanto acompanha sua jornada.
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/55">Acesso rápido</p>
              <p className="mt-3 text-lg font-semibold">Tudo começa aqui</p>
              <p className="mt-1 text-sm text-white/70">Entre no portal e escolha o caminho que faz sentido para a sua rotina.</p>
              <div className="mt-4 flex items-center gap-2 text-sm text-[#C0FF00]">
                <ArrowRight className="h-4 w-4" />
                Acompanhar sua jornada
              </div>
            </div>
            {welcomeVideo ? (
              <div className="rounded-3xl border border-white/10 bg-white/5 p-3">
                <TrainingVideoPlayer
                  video={welcomeVideo}
                  eyebrow="Boas-vindas"
                  ctaLabel="Assistir"
                  descriptionOverride="Vídeo de treinamento de boas-vindas com uma apresentação rápida do portal."
                  compact
                  surface="dark"
                  layout="stacked"
                  className="border border-white/10 bg-[#112746]/95 shadow-none"
                />
              </div>
            ) : null}
          </div>
        </section>

        <section className="px-6 py-8 sm:px-8 lg:px-10 lg:py-12">
          <div className="mx-auto max-w-md space-y-6">
            <div className="space-y-3 text-center lg:text-left">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#08213f] text-[#C0FF00] shadow-lg lg:mx-0">
                <Layers3 className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-3xl font-semibold text-[#08213f]">Portal da Jornada do Cliente</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  Use seu acesso para entrar no portal e continuar suas atividades com segurança.
                </p>
              </div>
            </div>

            <EnvironmentIndicator />

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-[#08213f]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoggingIn}
                  className="w-full"
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-[#08213f]">
                  Senha
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoggingIn}
                  className="w-full"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={isLoggingIn}
                className="h-12 w-full rounded-xl bg-[#C0FF00] font-semibold text-[#08213f] shadow-lg transition-all duration-200 hover:bg-[#a8e600] hover:shadow-xl"
              >
                {isLoggingIn ? "Entrando..." : "Entrar no portal"}
              </Button>
            </form>

            <div className="space-y-3 text-center lg:text-left">
              <button
                type="button"
                onClick={() => setLocation("/forgot-password")}
                className="block w-full text-sm text-[#08213f] transition-colors hover:underline lg:text-left"
              >
                Esqueci minha senha
              </button>
              <button
                type="button"
                onClick={() => setLocation("/first-access")}
                className="block w-full text-sm text-slate-500 transition-colors hover:text-[#08213f] hover:underline lg:text-left"
              >
                Primeiro acesso / Criar senha
              </button>
            </div>

            <div className="border-t border-slate-200 pt-4 text-center text-xs text-slate-500 lg:text-left">
              <p>© {new Date().getFullYear()} Portal da Jornada do Cliente — Ambiente de {environmentLabel}</p>
              <p>Versão {APP_VERSION}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
